import { useState, useEffect, useCallback } from 'react';
import {
  collection, query, where, orderBy,
  onSnapshot, addDoc, updateDoc, deleteDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './useAuth';

// ---------------------------------------------------------------------------
// usePantryCollections
// Real-time listener on pantryCollections for the current user.
// Mirrors iOS PantryViewModel — same collection, same field names.
//
// Firestore collection: pantryCollections
// Fields: userId, name, description, productIds, careProfileId,
//         careProfileName, icon, color, isPublic, createdAt, updatedAt
// ---------------------------------------------------------------------------

export const usePantryCollections = () => {
  const { user }  = useAuth();
  const [collections, setCollections] = useState([]);
  const [loading,     setLoading]     = useState(true);

  // ── Real-time listener ────────────────────────────────────────────────────

  useEffect(() => {
    if (!user) {
      setCollections([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'pantryCollections'),
      where('userId', '==', user.uid),
      orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        setCollections(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('usePantryCollections listener:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // ── Create ────────────────────────────────────────────────────────────────

  const createCollection = useCallback(async ({
    name,
    description    = null,
    icon           = 'cabinet',
    color          = null,
    careProfileId  = null,
    careProfileName = null,
  }) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const ref = await addDoc(collection(db, 'pantryCollections'), {
        userId:          user.uid,
        name:            name?.trim() || 'Untitled',
        description:     description  || null,
        productIds:      [],
        isPublic:        false,
        icon:            icon,
        color:           color        || null,
        careProfileId:   careProfileId   || null,
        careProfileName: careProfileName || null,
        createdAt:       new Date(),
        updatedAt:       new Date(),
      });
      return ref.id;
    } catch (err) {
      console.error('createCollection:', err);
      throw err;
    }
  }, [user]);

  // ── Update name/description ───────────────────────────────────────────────

  const updateCollection = useCallback(async (collectionId, { name, description }) => {
    if (!user || !collectionId) return;
    try {
      await updateDoc(doc(db, 'pantryCollections', collectionId), {
        ...(name        !== undefined && { name: name?.trim() }),
        ...(description !== undefined && { description }),
        updatedAt: new Date(),
      });
    } catch (err) {
      console.error('updateCollection:', err);
      throw err;
    }
  }, [user]);

  // ── Delete ────────────────────────────────────────────────────────────────

  const deleteCollection = useCallback(async (collectionId) => {
    if (!user || !collectionId) return;
    try {
      await deleteDoc(doc(db, 'pantryCollections', collectionId));
    } catch (err) {
      console.error('deleteCollection:', err);
      throw err;
    }
  }, [user]);

  // ── Add / remove products ─────────────────────────────────────────────────

  const addProduct = useCallback(async (collectionId, productId) => {
    if (!user || !collectionId || !productId) return;
    try {
      const target = collections.find(c => c.id === collectionId);
      const current = target?.productIds || [];
      if (current.includes(productId)) return;
      await updateDoc(doc(db, 'pantryCollections', collectionId), {
        productIds: [...current, productId],
        updatedAt:  new Date(),
      });
    } catch (err) {
      console.error('addProduct:', err);
      throw err;
    }
  }, [user, collections]);

  const removeProduct = useCallback(async (collectionId, productId) => {
    if (!user || !collectionId || !productId) return;
    try {
      const target  = collections.find(c => c.id === collectionId);
      const current = target?.productIds || [];
      await updateDoc(doc(db, 'pantryCollections', collectionId), {
        productIds: current.filter(id => id !== productId),
        updatedAt:  new Date(),
      });
    } catch (err) {
      console.error('removeProduct:', err);
      throw err;
    }
  }, [user, collections]);

  return {
    collections,
    loading,
    createCollection,
    updateCollection,
    deleteCollection,
    addProduct,
    removeProduct,
  };
};
